import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA",
  "ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK",
  "OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];

export default function CatalogRequestForm({ onSuccess, compact = false }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", street: "", city: "", state: "", zip: "",
    sms_opt_in: true, notes: "",
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) { setErr("Please enter your name."); return; }
    if (!form.phone.trim() && !form.email.trim()) {
      setErr("Please provide a phone number or email so we can reach you.");
      return;
    }
    if (!form.street.trim() || !form.city.trim() || !form.state || !form.zip.trim()) {
      setErr("Please complete the full mailing address — street, city, state and ZIP — so we can mail your catalog.");
      return;
    }
    if (!/^\d{5}(-\d{4})?$/.test(form.zip.trim())) {
      setErr("Please enter a valid ZIP code (e.g., 59101).");
      return;
    }
    setLoading(true);
    try {
      await api.post("/catalog-requests", form);
      setDone(true);
      onSuccess && onSuccess();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="catalog-request-success" className="text-center py-8">
        <div className="font-display text-4xl md:text-5xl mb-4" style={{ color: "var(--brass)" }}>Thank you.</div>
        <p className="text-base md:text-lg" style={{ color: "var(--text-secondary)" }}>
          Your catalog request is in. We&apos;ll be in touch{form.sms_opt_in && form.phone ? " — including by text" : ""}.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="catalog-request-form">
      <div className={compact ? "" : "grid grid-cols-1 md:grid-cols-2 gap-4"}>
        <Field label="Name *" testid="cr-name" value={form.name} onChange={set("name")} required />
        <Field label="Phone" testid="cr-phone" value={form.phone} onChange={set("phone")} type="tel" placeholder="(406) 555-0123" />
      </div>
      <Field label="Email" testid="cr-email" value={form.email} onChange={set("email")} type="email" />

      <Field label="Street Address * (for printed catalog)" testid="cr-street" value={form.street} onChange={set("street")} required placeholder="427 Peaceful Way" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="col-span-2">
          <Field label="City *" testid="cr-city" value={form.city} onChange={set("city")} required />
        </div>
        <label className="block">
          <span className="eyebrow-muted text-[10px] block mb-2">State *</span>
          <select
            data-testid="cr-state"
            value={form.state}
            onChange={(e) => set("state")(e.target.value)}
            required
            className="w-full h-11 px-2 bg-transparent border outline-none focus:border-[color:var(--brass)]"
            style={{ borderColor: "var(--border-subtle)", color: form.state ? "var(--text-primary)" : "var(--text-muted)" }}
          >
            <option value="">—</option>
            {US_STATES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <Field label="ZIP *" testid="cr-zip" value={form.zip} onChange={set("zip")} required placeholder="59101" inputMode="numeric" maxLength={10} />
      </div>

      <label className="flex items-start gap-3 cursor-pointer pt-2">
        <input
          type="checkbox"
          checked={form.sms_opt_in}
          onChange={(e) => set("sms_opt_in")(e.target.checked)}
          data-testid="cr-sms-opt-in"
          className="mt-1 h-4 w-4 accent-[#1E4D75]"
        />
        <span className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>
          Text me sale updates, catalog drop notices, and reminders. Message & data rates may apply. Reply STOP anytime.
        </span>
      </label>

      <Field label="Anything specific you're looking for?" testid="cr-notes" value={form.notes} onChange={set("notes")} textarea />

      {err && <div data-testid="catalog-request-error" className="text-sm" style={{ color: "#E5807F" }}>{err}</div>}

      <button data-testid="catalog-request-submit" type="submit" disabled={loading} className="btn-primary w-full sm:w-auto">
        {loading ? "Sending…" : "Request Catalog →"}
      </button>
      <p className="text-[11px] leading-relaxed pt-2" style={{ color: "var(--text-muted)" }}>
        Or call the sale office direct: <a href="tel:208-267-2668" className="link-brass">208-267-2668</a>
      </p>
    </form>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, required, textarea, testid, inputMode, maxLength }) {
  const Comp = textarea ? "textarea" : "input";
  return (
    <label className="block">
      <span className="eyebrow-muted text-[10px] block mb-2">{label}</span>
      <Comp
        data-testid={testid}
        type={textarea ? undefined : type}
        rows={textarea ? 3 : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        inputMode={inputMode}
        maxLength={maxLength}
        className={`w-full ${textarea ? "py-3" : "h-11"} px-3 bg-transparent border outline-none focus:border-[color:var(--brass)]`}
        style={{ borderColor: "var(--border-subtle)", color: "var(--text-primary)" }}
      />
    </label>
  );
}
